import { useCallback } from 'react';
import type { CustomerCardSaveData } from '@makemydeal/dr-activities-customer-card';
import {
    StyledDealSetupActivity,
    StyledDealSetupCard,
    StyledDealSetupHeader,
    StyledDealSetupTitle,
    StyledDealSetupDescription
} from './DealSetupScreen.style';
import { DealSetupDealershipSection } from './DealSetupDealershipSection';
import { DealSetupCustomerInfoSection } from './DealSetupCustomerInfoSection';
import { DealSetupVehicleSection } from './DealSetupVehicleSection';
import { DealSetupFooter } from './DealSetupFooter';
import FullPageLoader from '../FullPageLoader/FullPageLoader';
import { useDealSetup, useDealSetupLaunchDeal } from '../../hooks';
import { useDispatch } from 'react-redux';
import { setWorkflowEvaluation } from '@makemydeal/dr-sales-checkin-store';

/**
 * DealSetupScreen — Pre-dashboard screen for Sales Check-In.
 *
 * Flow:
 *  1. Accordions disabled/collapsed until dealer settings arrive (hasDealerSettings)
 *  2. When Deal Setup is required: expands Customer + Vehicle, marks fields mandatory
 *  3. When it is not: keeps sections collapsed, fields optional
 *
 * Customer save is owned by the Deal Central customer card: the footer's Launch triggers the
 * card's `apply()`, and the card's `onSave` reconciles the buyer into SCI state and continues
 * the launch (vehicle + navigation).
 */
const DealSetupScreen: React.FC = () => {
    const dispatch = useDispatch();
    const { isDealSetupRequired, showroomCustomerOverride, hasValidCustomer, customerCardControls, sectionToggleControls } =
        useDealSetup();
    const { cardRef, cardState, handleCardStateChange } = customerCardControls;
    const { launchDeal, handleCardSave, isLaunching } = useDealSetupLaunchDeal();

    const handleLaunch = useCallback(() => {
        dispatch(setWorkflowEvaluation(true));
        if (!isDealSetupRequired || !cardState?.canApply) {
            launchDeal(showroomCustomerOverride);
            return;
        }
        cardRef.current?.apply();
    }, [isDealSetupRequired, launchDeal, showroomCustomerOverride, cardState?.canApply, cardRef]);

    const handleCustomerCardSave = (saveData: CustomerCardSaveData) => {
        sectionToggleControls.resetVehicleAndCustomerInfo();
        handleCardSave(saveData);
    };

    return (
        <>
            <StyledDealSetupActivity data-testid="deal-setup-screen">
                <StyledDealSetupCard>
                    <StyledDealSetupHeader>
                        <StyledDealSetupTitle>Deal Setup</StyledDealSetupTitle>
                        <StyledDealSetupDescription>
                            Select a dealership, enter customer information, and choose a vehicle to get started.
                        </StyledDealSetupDescription>
                    </StyledDealSetupHeader>
                    <DealSetupDealershipSection />
                    {isDealSetupRequired && (
                        <>
                            <DealSetupCustomerInfoSection
                                cardRef={cardRef}
                                onCardSave={handleCustomerCardSave}
                                onCardStateChange={handleCardStateChange}
                                expanded={sectionToggleControls.customerInfoExpanded}
                                onToggle={sectionToggleControls.toggleCustomerInfo}
                            />
                            <DealSetupVehicleSection
                                expanded={sectionToggleControls.vehicleInfoExpanded}
                                onToggle={sectionToggleControls.toggleVehicleInfo}
                            />
                        </>
                    )}
                </StyledDealSetupCard>
            </StyledDealSetupActivity>
            <DealSetupFooter
                onLaunch={handleLaunch}
                isLaunchInProgress={isLaunching || Boolean(cardState?.isSaving)}
                isCustomerReady={Boolean(cardState?.canApply) || hasValidCustomer}
            />
            {/* Show the "Updating deal" overlay while the customer card is saving, before UPDATE_DEAL's
                own global loader takes over. */}
            {Boolean(cardState?.isSaving) && <FullPageLoader message="Updating deal" />}
        </>
    );
};

export default DealSetupScreen;
